// src/components/main/Harvest.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  useTheme,
  Skeleton,
  Button,
  List,
  ListItem,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ThirdLoader from '../loaders/ThirdLoader';

export default function Harvest({ bottles }) {
  const theme = useTheme();
  const navigate = useNavigate();

  // loading skeleton
  const [loading, setLoading] = useState(true);
  // nav loader
  const [navLoading, setNavLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  const handleAvailability = (id) => {
    setNavLoading(true);
    setTimeout(() => {
      navigate(`/products/${id}`);
    }, 2000); // keep loader up for 2s
  };

  const detailsMap = {
    1: [
      { label: 'Batch Doukkala',  color: theme.palette.primary.main },
      { label: 'Organic',         color: theme.palette.secondary.main },
      { label: 'Medium',          color: '#81b29a' },
      { label: 'Monovarietal', color: '#e07a5f' },
    ],
    2: [
      { label: 'Batch Doukkala', color: theme.palette.primary.main },
      { label: 'Organic',         color: theme.palette.secondary.main },
      { label: 'Medium',          color: '#81b29a' },
      { label: 'Monovarietal',    color: '#e07a5f' },
    ],
  };

  const cardVariants = {
    hidden:   { opacity: 0, y: 80 },
    visible:  { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
    hover:    { scale: 1.15, y: -30, transition: { duration: 0.3 } },
  };

  const badgeVariants = {
    hidden: { scale: 1, rotate: 0 },
    hover:  { scale: 1.4, rotate: 360, transition: { duration: 0.6, ease: 'easeInOut' } },
  };

  return (
    <>
      {/* Secondary loader for navigation */}
      <AnimatePresence>
        {navLoading && <ThirdLoader key="third-loader" />}
      </AnimatePresence>

      <Container
        id="products"
        component="section"
        sx={{
          backgroundColor: theme.palette.background.default,
          mt: { xs: 4, md: -2 },
          mb: { xs: 4, md: 6 },
        }}
      >
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            mb: { xs: 8, md: 6 },
            color: theme.palette.text.primary,
          }}
        >
          Harvest&nbsp;2024
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 6, md: 10 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' },
            justifyItems: 'center',
          }}
        >
          {(loading ? Array(bottles.length).fill({}) : bottles).map((b, i) => (
            <Box
              key={i}
              component={motion.div}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, amount: 0.4 }}
              sx={{
                width: 360,
                perspective: 1400,
                '&:hover .infoPanel': {
                  opacity: 1,
                  transform: 'translate(-50%, 0)',
                  pointerEvents: 'auto',
                },
                '&:hover .staticTitle': {
                  opacity: 0,
                },
              }}
            >
              {/* Card */}
              <Box
                sx={{
                  position: 'relative',
                  width: 320,
                  height: 480,
                  mx: 'auto',
                  mt: 12,
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[50],
                  border: '1px solid #EEF2F5',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  overflow: 'visible',
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" height={350} animation="wave" />
                ) : (
                  <>
                    <Box
                      component="img"
                      src={b.img}
                      alt={b.name}
                      sx={{
                        position: 'absolute',
                        top: -120,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        maxHeight: 600,
                        objectFit: 'contain',
                        width: 'auto',
                      }}
                    />
                    <Box
                      component={motion.div}
                      variants={badgeVariants}
                      initial="hidden"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.secondary.main,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        textAlign: 'center',
                        lineHeight: 1.2,
                        pointerEvents: 'none',
                      }}
                    >
                      Hand<br />Press
                    </Box>
                  </>
                )}

                {/* Info Panel */}
                {!loading && (
                  <Box
                    className="infoPanel"
                    sx={{
                      position: 'absolute',
                      bottom: -160,
                      left: '50%',
                      transform: 'translate(-50%, 100%)',
                      width: 340,
                      backgroundColor: 'rgba(255,255,255,0.98)',
                      borderRadius: 2,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      opacity: 0,
                      transition: 'opacity 0.6s ease, transform 0.6s ease',
                      zIndex: 10,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box sx={{ px: 3, py: 2.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 600,
                          mb: 1,
                          textAlign: 'center',
                          fontSize: '1.25rem',
                        }}
                      >
                        {b.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          textAlign: 'center',
                          fontStyle: 'italic',
                          fontSize: '0.9rem',
                        }}
                      >
                        {b.volume}
                      </Typography>
                      <List disablePadding>
                        {detailsMap[b.id]?.map((item) => (
                          <ListItem
                            key={item.label}
                            sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: item.color,
                                mr: 1.5,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.9rem', color: theme.palette.text.primary }}
                            >
                              {item.label}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: theme.palette.text.secondary,
                          fontSize: '0.9rem',
                        }}
                      >
                        This extra-virgin olive oil features a bright, fruity aroma with just a hint of pepper on the finish. Perfect for drizzling over salads or finishing grilled vegetables.
                      </Typography>

                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          mt: 2.5,
                          fontSize: '1rem',
                          py: 1.5,
                          backgroundColor: theme.palette.common.black,
                          color: theme.palette.common.white,
                          '&:hover': {
                            backgroundColor: theme.palette.grey[900],
                          },
                        }}
                        onClick={() => handleAvailability(b.id)}
                      >
                        Check Availability
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Static Title */}
              {!loading && (
                <Typography
                  className="staticTitle"
                  variant="subtitle1"
                  sx={{
                    mt: 8,
                    mb: 8,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    cursor: 'default',
                    transition: 'opacity 0.3s ease',
                    fontSize: '1.1rem',
                  }}
                >
                  {b.name}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </>
  );
}
